
import React, { useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { Typography, Box, Stack } from "@mui/material";
import {
  Chart as ChartJS,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  Title,
} from "chart.js";
import Cutout from "../../assets/img/cutout.png";
import Cutout2 from "../../assets/img/cutout-compact.png";
import Star from "../../assets/img/Star.png";
import Outline_Star from "../../assets/img/Outline_Star.png";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
  ChartDataLabels
);

const CHART_FONT = "Arial, Helvetica, sans-serif";
const CHART_GOLD = "#A68900";
const SCORE_COLOR = "#002D56";
const LABEL_COLOR = "#999999";
const GRID_COLOR = "#E0E0E0";

const CHART_CATEGORY_LABELS = [
  "Inherent Risks",
  "Assets Protection",
  "Management & people practices",
  "Special Hazards",
  "Natural Hazards",
];

// Fixed 14.5 cm × 4.35 cm at 96 DPI — same size on every screen
const CM_TO_PX = 96 / 2.54;
export const RATE_CHART_WIDTH_CM = 17;
export const RATE_CHART_HEIGHT_CM = 5.15;
export const RATE_CHART_WIDTH_PX = Math.round(RATE_CHART_WIDTH_CM * CM_TO_PX);
export const RATE_CHART_HEIGHT_PX = Math.round(RATE_CHART_HEIGHT_CM * CM_TO_PX);

const RATING_LEFT_PX = Math.round(RATE_CHART_WIDTH_PX * 0.036);
const RATING_TOP_PX = Math.round(RATE_CHART_HEIGHT_PX * 0.22);
const RATING_WIDTH_PX = Math.round(RATE_CHART_WIDTH_PX * 0.32);
const SCORE_FONT_PX = Math.round(RATE_CHART_HEIGHT_PX * 0.32);
const SCORE_PADDING_TOP_PX = Math.round(RATE_CHART_HEIGHT_PX * 0.15);
const STAR_SIZE_PX = Math.round(RATE_CHART_HEIGHT_PX * 0.15);

const CHART_SLOT_LEFT_PX = Math.round(RATE_CHART_WIDTH_PX * 0.37);
const CHART_SLOT_TOP_PX = Math.round(RATE_CHART_HEIGHT_PX * 0.265);
const CHART_SLOT_RIGHT_PX = Math.round(RATE_CHART_WIDTH_PX * 0.023);
const CHART_SLOT_BOTTOM_PX = Math.round(RATE_CHART_HEIGHT_PX * 0.075);
const CHART_PLOT_WIDTH_PX =
  RATE_CHART_WIDTH_PX - CHART_SLOT_LEFT_PX - CHART_SLOT_RIGHT_PX;
const CHART_PLOT_HEIGHT_PX =
  RATE_CHART_HEIGHT_PX - CHART_SLOT_TOP_PX - CHART_SLOT_BOTTOM_PX;

const COMPACT_CHART_SLOT_TOP_PX = Math.round(RATE_CHART_HEIGHT_PX * 0.2);
const COMPACT_CHART_SLOT_LEFT_PX = Math.round(RATE_CHART_WIDTH_PX * 0.025);
const COMPACT_CHART_SLOT_RIGHT_PX = Math.round(RATE_CHART_WIDTH_PX * 0.02);
const COMPACT_CHART_SLOT_BOTTOM_PX = Math.round(RATE_CHART_HEIGHT_PX * 0.08);
const COMPACT_CHART_PLOT_WIDTH_PX =
  RATE_CHART_WIDTH_PX - COMPACT_CHART_SLOT_LEFT_PX - COMPACT_CHART_SLOT_RIGHT_PX;
const COMPACT_CHART_PLOT_HEIGHT_PX =
  RATE_CHART_HEIGHT_PX -
  COMPACT_CHART_SLOT_TOP_PX -
  COMPACT_CHART_SLOT_BOTTOM_PX;

const fixedFrameStyle = {
  position: "relative",
  width: RATE_CHART_WIDTH_PX,
  height: RATE_CHART_HEIGHT_PX,
  minWidth: RATE_CHART_WIDTH_PX,
  maxWidth: RATE_CHART_WIDTH_PX,
  minHeight: RATE_CHART_HEIGHT_PX,
  maxHeight: RATE_CHART_HEIGHT_PX,
  flexShrink: 0,
  boxSizing: "border-box",
  overflow: "hidden",
};

const getBarChartOptions = (tickFontSize = 10, compact = false) => ({
  indexAxis: "y",
  responsive: false,
  maintainAspectRatio: false,
  layout: {
    padding: { top: 2, right: 4, bottom: 2, left: 0 },
  },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false },
    datalabels: { display: false },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: GRID_COLOR,
        drawOnChartArea: true,
        drawTicks: false,
        offset: false,
        lineWidth: (context) => {
          const { index } = context;
          const count = context.chart.scales.x.ticks.length;
          return index === 0 || index === count ? 0 : 1;
        },
      },
      border: { display: false },
      min: 0,
      max: 5,
      ticks: {
        color: LABEL_COLOR,
        font: { size: tickFontSize, family: CHART_FONT, weight: "normal" },
        callback(value) {
          if (value === 0) return "";
          return `${value} Stars`;
        },
        stepSize: 1,
        align: "center",
      },
    },
    y: {
      grid: { display: false },
      border: { display: false },
      ticks: {
        align: "end",
        color: LABEL_COLOR,
        font: { size: tickFontSize, family: CHART_FONT, weight: "normal" },
        padding: 4,
      },
      afterFit: (scaleInstance) => {
        scaleInstance.paddingTop = compact ? 0 : 8;
        scaleInstance.paddingBottom = compact ? 0 : 8;
      },
    },
  },
});

/** Comprehensive: fixed 14.5×4.35 cm frame with score + chart columns */
function ComprehensiveRateChart({ roundedScore, filledStars, emptyStars, chartBarData }) {
  return (
    <div className="chart-table" style={fixedFrameStyle}>
      <img
        src={Cutout}
        alt="Executive summary chart"
        width={RATE_CHART_WIDTH_PX}
        height={RATE_CHART_HEIGHT_PX}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: RATE_CHART_WIDTH_PX,
          height: RATE_CHART_HEIGHT_PX,
          display: "block",
          pointerEvents: "none",
          userSelect: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: RATING_TOP_PX,
          left: RATING_LEFT_PX,
          width: RATING_WIDTH_PX,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          pointerEvents: "none",
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: SCORE_FONT_PX,
            lineHeight: 1,
            fontWeight: "bold",
            color: SCORE_COLOR,
            fontFamily: CHART_FONT,
            m: 0,
            p: 0,
            pt: `${SCORE_PADDING_TOP_PX}px`,
          }}
        >
          {roundedScore}
        </Typography>
        <Stack
          direction="row"
          sx={{
            mt: "4px",
            gap: "3px",
            "& img": { width: STAR_SIZE_PX, height: STAR_SIZE_PX, objectFit: "contain" },
          }}
        >
          {[...Array(filledStars)].map((_, index) => (
            <Box key={`filled-${index}`} component="img" src={Star} alt="" />
          ))}
          {[...Array(emptyStars)].map((_, index) => (
            <Box key={`empty-${index}`} component="img" src={Outline_Star} alt="" />
          ))}
        </Stack>
      </div>
      <div
        style={{
          position: "absolute",
          top: CHART_SLOT_TOP_PX,
          left: CHART_SLOT_LEFT_PX,
          width: CHART_PLOT_WIDTH_PX,
          height: CHART_PLOT_HEIGHT_PX,
        }}
      >
        <Bar
          options={getBarChartOptions(9)}
          data={chartBarData}
          width={CHART_PLOT_WIDTH_PX}
          height={CHART_PLOT_HEIGHT_PX}
        />
      </div>
    </div>
  );
}

/** Compact: fixed 14.5×4.35 cm frame, bars span full cutout width */
function CompactRateChart({ compactChartData }) {
  return (
    <div className="chart-table" style={fixedFrameStyle}>
      <img
        src={Cutout2}
        alt="Executive summary compact chart"
        width={RATE_CHART_WIDTH_PX}
        height={RATE_CHART_HEIGHT_PX}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: RATE_CHART_WIDTH_PX,
          height: RATE_CHART_HEIGHT_PX,
          display: "block",
          pointerEvents: "none",
          userSelect: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: COMPACT_CHART_SLOT_TOP_PX,
          left: COMPACT_CHART_SLOT_LEFT_PX,
          width: COMPACT_CHART_PLOT_WIDTH_PX,
          height: COMPACT_CHART_PLOT_HEIGHT_PX,
        }}
      >
        <Bar
          options={getBarChartOptions(8, true)}
          data={compactChartData}
          width={COMPACT_CHART_PLOT_WIDTH_PX}
          height={COMPACT_CHART_PLOT_HEIGHT_PX}
        />
      </div>
    </div>
  );
}

export default function RateChart({
  setOverallScore,
  overallScore,
  formData,
  showFullChart,
  imagetext = false,
}) {
  const mflvalue = (mfl) => {
    if (mfl <= 50) return 16;
    if (mfl > 50 && mfl <= 75) return 14;
    return 12;
  };

  const SelectStar = (value) => {
    if (value <= 49) return 1;
    if (value <= 64) return 2;
    if (value <= 79) return 3;
    if (value <= 89) return 4;
    return 5;
  };

  const data = useMemo(
    () => ({
      labels: CHART_CATEGORY_LABELS,
      datasets: [
        {
          label: "Rating",
          data: [
            SelectStar(
              (Math.round(
                formData?.star_rating_page1?.construction_category?.value +
                  mflvalue(formData?.mfl_calculation?.mfl_estimate_precentage) +
                  formData?.star_rating_page1?.predominant_construction?.value
              ) /
                80) *
                100
            ),
            SelectStar(
              Math.round(
                (formData?.fire_detection_protection?.available_score / 30) * 100
              )
            ),
            SelectStar(
              Math.round(
                (formData?.management_practice?.available_score / 30) * 100
              )
            ),
            SelectStar(
              Math.round((formData?.special_hazard?.available_score / 20) * 100)
            ),
            SelectStar(
              Math.round(
                ((formData?.natural_hazards_exposure?.available_score ?? 0) /
                  40) *
                  100 +
                  ((formData?.natural_hazards_recommendation?.available_score ??
                    0) /
                    40) *
                    100
              ) || 0
            ),
          ],
          backgroundColor: CHART_GOLD,
          borderColor: CHART_GOLD,
          borderWidth: 0,
          barThickness: 6,
        },
      ],
    }),
    [formData]
  );

  const total_available_s =
    formData?.star_rating_page1?.construction_category?.value +
    mflvalue(formData?.mfl_calculation?.mfl_estimate_precentage) +
    formData?.star_rating_page1?.predominant_construction?.value +
    formData?.natural_hazards_exposure?.available_score;

  const current_score =
    ((formData?.fire_detection_protection?.available_score +
      formData?.management_practice?.available_score +
      formData?.special_hazard?.available_score +
      formData?.natural_hazards_recommendation?.available_score) /
      100) *
    total_available_s;

  useEffect(() => {
    setOverallScore(SelectStar((current_score / total_available_s) * 100));
  }, [data, current_score, total_available_s, setOverallScore]);

  const roundedScore = Math.round(overallScore);
  const filledStars = Math.floor(roundedScore);
  const emptyStars = 5 - filledStars;

  const chartBarData = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      minBarLength: 2,
      barPercentage: 0.75,
      categoryPercentage: 0.85,
    })),
  };

  const compactChartData = {
    labels: CHART_CATEGORY_LABELS,
    datasets: [
      {
        data: data.datasets[0].data,
        backgroundColor: CHART_GOLD,
        borderColor: CHART_GOLD,
        borderWidth: 0,
        maxBarThickness: 6,
        minBarLength: 2,
        barPercentage: 0.75,
        categoryPercentage: 0.85,
      },
    ],
  };

  const wrapperStyle = imagetext
    ? {
        width: RATE_CHART_WIDTH_PX,
        height: RATE_CHART_HEIGHT_PX,
        minWidth: RATE_CHART_WIDTH_PX,
        minHeight: RATE_CHART_HEIGHT_PX,
        overflow: "visible",
        flexShrink: 0,
      }
    : {
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        display: "flex",
        justifyContent: "center",
      };

  return (
    <div style={wrapperStyle}>
      {showFullChart ? (
        <ComprehensiveRateChart
          roundedScore={roundedScore}
          filledStars={filledStars}
          emptyStars={emptyStars}
          chartBarData={chartBarData}
        />
      ) : (
        <CompactRateChart compactChartData={compactChartData} />
      )}
    </div>
  );
}
